import React, { useState } from 'react'
import { useMutation } from '@tanstack/react-query'
import { handlePostRequest } from '../../../utils/api-handler.util'
import RespondFinderSuccessModal from './RespondFinderSuccessModal'
import { TagIcon } from '@heroicons/react/24/outline'
import { useNavigate } from 'react-router-dom'
import ProductPickerModal from './ProductPickerModal'
import useProductCategories from '../../../hooks/useProductCategories'
import { CheckCircleIcon } from '@heroicons/react/24/solid'
import Modal from '../../common/Modal'
import { QueryKeys } from '../../../utils/queryKeyGenerator'
import productRequestTypes from '../../../constants/productRequestTypes'

const RespondFinderModal = ({ isOpen, onClose, request }) => {
  const { getCategoryNameById } = useProductCategories()
  const [price, setPrice] = useState('')
  const [showSuccess, setShowSuccess] = useState(false)
  const [newBidRequestId, setNewBidRequestId] = useState(null)
  const [showProductPicker, setShowProductPicker] = useState(false)
  const [selectedProduct, setSelectedProduct] = useState(null)
  const navigate = useNavigate()

  const respondMutation = useMutation({
    mutationFn: (payload) => handlePostRequest(`/retailers/bid-requests/respond`, payload),
    onSuccess: (response) => {
      setNewBidRequestId(response?.data?.bidRequestId)
      QueryKeys.retailerBidRequests.invalidate(productRequestTypes.all.key, 1)
      setShowSuccess(true)
    },
  })

  if (!isOpen) return null

  const handleSubmit = async (e) => {
    e.preventDefault()
    await respondMutation.mutateAsync({
      productId: selectedProduct?.productId,
      productFinderId: request.productFinderId,
      finalPrice: Number(price),
      bidRequestId: request.id,
    })
  }

  const handleProductSelect = (product) => {
    setSelectedProduct(product)
    setShowProductPicker(false)
  }

  const renderSelectedProduct = () => {
    if (!selectedProduct) return null

    return (
      <>
        <div className="flex items-center justify-between w-full p-2 border border-gray-300 rounded-md cursor-pointer">
          <div className="flex items-center">
            <img
              src={selectedProduct?.primaryImageUrl || '/images/default-product.png'}
              alt={selectedProduct?.name}
              className="w-16 h-16 object-cover rounded-md mr-4"
            />
            <div className="flex flex-col justify-start items-start">
              <span className="text-md font-bold  text-gray-800">
                {selectedProduct?.name || 'No Name'}
              </span>
              <span className="text-xs text-gray-500">
                {getCategoryNameById(selectedProduct?.categoryId) || 'Uncategorized'}
              </span>
              <span className="text-xs text-gray-500">{selectedProduct.priceText}</span>
            </div>
          </div>
          <CheckCircleIcon className="h-6 w-6 text-green-600" />
        </div>
        <div className="flex w-full justify-end mt-2">
          <button
            className="text-sm text-secondary-600 underline "
            onClick={() => setSelectedProduct(null)}
          >
            remove product
          </button>
        </div>
      </>
    )
  }

  if (showProductPicker) {
    return (
      <ProductPickerModal
        onClose={() => setShowProductPicker(false)}
        onSelect={handleProductSelect}
        finderCategoryId={request.categoryId}
      />
    )
  }

  if (showSuccess) {
    return (
      <RespondFinderSuccessModal
        isOpen={showSuccess}
        onClose={() => setShowSuccess(false)}
        onViewResponse={() => {
          setShowSuccess(false)
          onClose(true)
          if (newBidRequestId) {
            navigate(`/retailer/products/requests/${request.id}`)
          }
        }}
      />
    )
  }

  return (
    <>
      <Modal isOpen={isOpen} onClose={onClose}>
        <div className="p-6">
          <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-green-100 mb-4 mt-2">
            <TagIcon className="h-10 w-10 text-green-600" />
          </div>
          <h3 className="text-center text-[19.2px] font-bold mb-2 mt-2 capitalize">
            Respond Product Finder
          </h3>
          <p className="text-center text-[13.5px] text-black/70 mb-6">
            Customers can submit Product Bidding to notify all retailers offering that product that
            they are interested in receiving bids.
          </p>
          <div className="flex flex-col items-center mb-4">
            <div className="text-lg font-medium text-gray-800 text-center mb-4">
              {request.description}
            </div>
            {!selectedProduct?.id && (
              <button
                type="button"
                className="w-full h-[55px] bg-gradient-to-r from-[#D17707] to-[#E88F20] text-white text-lg font-medium rounded-[10px] shadow-md mb-4"
                onClick={() => setShowProductPicker(true)}
              >
                Select Product
              </button>
            )}
            {renderSelectedProduct()}
          </div>
          <form onSubmit={handleSubmit} className="flex flex-col flex-1">
            <label className="font-medium text-[16px] text-black/70 mb-1">
              Final Price <span className="text-red-500">*</span>
            </label>
            <input
              className="w-full border rounded px-4 py-2 mb-2 bg-[#F5EFE7]"
              type="number"
              placeholder="Enter price"
              value={price}
              onChange={(e) => setPrice(e.target.value)}
              disabled={respondMutation.isLoading}
              {...(selectedProduct?.id ? { max: selectedProduct?.price } : {})}
              required
            />
            <button
              type="submit"
              className="w-full h-[55px] mt-8 bg-gradient-to-r from-[#D17707] to-[#E88F20] text-white text-lg font-medium rounded-[10px] shadow-md disabled:opacity-60"
              disabled={respondMutation.isLoading}
            >
              {respondMutation.isLoading ? 'Submitting...' : 'Submit'}
            </button>
          </form>
        </div>
      </Modal>
    </>
  )
}

export default RespondFinderModal
